import React, { useEffect, useState, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { Image, Typography, LoadingIndicator, Pagination } from 'lib/components';
import { useAppDispatch, actions, useSelectServices, useSelectRequest, RequestManager } from 'store';
import { Service } from 'types';
import { routes } from 'lib/api';
import { useCustomToast, usePagination } from 'hooks';
import classes from './index.module.scss';

/**
 * Props interface for the TableRow component
 */
interface TableRowProps {
	/** Service object containing service data */
	service: Service;
	/** Callback function when delete button is clicked */
	onDelete: (id: number) => void;
}

/**
 * TableHeader component
 * Renders the header row for the services table
 * Displays column titles for service name and description
 *
 * @returns {JSX.Element} - Rendered component
 */
const TableHeader = () => (
	<div className={classes['servicesTab-tableHeader']}>
		{/* Service title column */}
		<div>
			<Typography color="darkgrey" variant="h6">
				Title
			</Typography>
		</div>
		{/* Service description column */}
		<div>
			<Typography color="darkgrey" variant="h6">
				Description
			</Typography>
		</div>
	</div>
);

/**
 * TableRow component
 * Renders a row in the services table for a single service
 * Displays service name, description, and action buttons (edit/delete)
 *
 * @param {TableRowProps} props - Component props
 * @returns {JSX.Element} - Rendered component
 */
const TableRow = ({ service, onDelete }: TableRowProps) => {
	// Navigation helper
	const { push } = useHistory();

	/**
	 * Navigate to the edit service page for this service
	 * Memoized with useCallback to prevent unnecessary re-renders
	 */
	const navToEditService = useCallback(() => {
		push(`/settings/services/edit/${service.id}`);
	}, [service]);

	return (
		<div className={classes['servicesTab-tableRow']}>
			{/* Service name column */}
			<div>
				<Typography variant="h6">{service.name}</Typography>
			</div>
			{/* Service description column */}
			<div>
				<Typography variant="h6">{service.description}</Typography>
			</div>
			{/* Action buttons column */}
			<div className={classes['servicesTab-tableRowActions']}>
				{/* Edit button */}
				<Image src="icEdit" onClick={navToEditService} />
				{/* Delete button */}
				<Image src="icTrashDanger" onClick={() => onDelete(service.id)} />
			</div>
		</div>
	);
};

/**
 * ServicesTab component
 * Displays and manages services in the settings screen
 * Allows viewing, adding, editing, and deleting services
 *
 * @returns {JSX.Element} - Rendered component
 */
const ServicesTab = () => {
	// Custom toast notifications for user feedback
	const toast = useCustomToast();
	// Navigation helper
	const { push } = useHistory();

	// Redux state and actions
	const dispatch = useAppDispatch();
	// Active API request tracking
	const { active } = useSelectRequest();
	// Services data from Redux store
	const services = useSelectServices();

	// ID of the service being deleted, -1 means no deletion in progress
	const [toDelete, setToDelete] = useState(-1);

	/**
	 * Fetch services data on component mount
	 */
	useEffect(() => {
		dispatch(actions.fetchServices());
	}, []);

	/**
	 * Set up pagination for services list
	 * Returns data and handlers for paginated display with 8 items per page
	 */
	const { isDataFetched, listing, paginatedData, paginationProps } = usePagination(services, { itemsPerPage: 8 });

	/**
	 * Monitor API requests for service deletion
	 * Shows toast notifications for success/failure and resets deletion state
	 */
	useEffect(() => {
		const RM = new RequestManager(active);
		if (RM.idle()) return;

		if (toDelete !== -1 && RM.req(routes.SERVICE_ID(toDelete), 'DELETE')) {
			if (RM.failed()) {
				toast(active.message, 'error');
			} else if (RM.succeeded()) {
				toast(active.message);
				// Reset deletion state after successful deletion
				setToDelete(-1);
			}
		}
	}, [active]);

	/**
	 * Navigate to the add service page
	 * Memoized with useCallback to prevent unnecessary re-renders
	 */
	const navToAddService = useCallback(() => {
		push('/settings/services/add');
	}, []);

	/**
	 * Handle service deletion
	 * Sets the service ID to delete and dispatches the delete action
	 *
	 * @param {number} id - ID of the service to delete
	 */
	const handleDelete = (id: number) => {
		setToDelete(id);
		dispatch(actions.deleteService(id));
	};

	// Show loading indicator while data is being fetched
	if (!isDataFetched) return <LoadingIndicator />;

	return (
		<>
			<div className={classes['servicesTab']}>
				{/* Services table container */}
				<div className={classes['servicesTab-table']}>
					{/* Only show table header if there are services to display */}
					{listing.length > 0 && <TableHeader />}
					{/* Table body with service rows */}
					<div className={classes['servicesTab-tableBody']}>
						{/* Map through paginated services and render a row for each */}
						{paginatedData.map((service) => (
							<TableRow key={service.id} service={service} onDelete={handleDelete} />
						))}
					</div>
					{/* Table footer with add button */}
					<div className={classes['servicesTab-tableFooter']}>
						<a onClick={navToAddService} className={classes['servicesTab-tableFooterButton']}>
							+ Add New Service
						</a>
					</div>
				</div>
			</div>
			{/* Pagination controls */}
			<Pagination {...paginationProps} />
		</>
	);
};

export default ServicesTab;
